import { useMemo } from 'react';
import { getGameMetadata, isRuntimeStatus } from '@swtorbit/shared-socket-events';
import { Dropdown, DropdownDivider, DropdownHeading, DropdownItem } from '@swtorbit/ui/react';
import { useGameContext } from '../contexts/GameContext';
import { formatDay } from '../utils/formatDate';

interface GameSelectorDropdownProps {
  onViewAllGames: () => void;
  onCreateGame: () => void;
}

export function GameSelectorDropdown({ onViewAllGames, onCreateGame }: GameSelectorDropdownProps) {
  const { games, selectedGame, selectGame } = useGameContext();

  const recentGames = useMemo(() => {
    const sorted = [...games].sort((a, b) => {
      const aRunning = isRuntimeStatus(a.status);
      const bRunning = isRuntimeStatus(b.status);
      if (aRunning && !bRunning) return -1;
      if (bRunning && !aRunning) return 1;
      return b.createdAt - a.createdAt;
    });
    return sorted.slice(0, 4);
  }, [games]);

  return (
    <Dropdown label={<span className="visually-hidden">Select game</span>} trigger={{ variant: 'outline' }}>
      {recentGames.length === 0 ? (
        <DropdownHeading>No games yet</DropdownHeading>
      ) : (
        recentGames.map((game) => (
          <DropdownItem
            key={game.dbId}
            selected={selectedGame?.dbId === game.dbId}
            onClick={() => selectGame(game)}
          >
            <span className="ff-mono">{game.roomCode}</span>
            <span className="text-slate">·</span>
            {/* Codes are reused once released: the date tells same-code games apart. */}
            <span className="text-slate">{formatDay(game.startedAt ?? game.createdAt)}</span>
            <span className="text-slate">·</span>
            <span className="fw-bold">{getGameMetadata(game.gameType).title}</span>
          </DropdownItem>
        ))
      )}

      <DropdownDivider />

      <DropdownItem hue="cyan" onClick={onCreateGame}>+ Create New Game</DropdownItem>

      <DropdownDivider />

      <DropdownItem onClick={onViewAllGames}>View All Games →</DropdownItem>
    </Dropdown>
  );
}
